import { PropsWithChildren, ReactNode } from 'react';
import { GetStaticProps, NextPage } from 'next';
import { BASE_PATHS, getRandomSampleFromArray } from '@belong/common';
import { PrismicDocument } from '@prismicio/types';
import { kebabCase } from 'es-toolkit/compat';
import { fetchNeighborhoodService } from 'src/api/geo';
import { fetchHomesService } from 'src/api/homes';
import { fetchCityImageService } from 'src/api/medias';
import {
  CoverageRegionModel,
  UnitListingModel,
  CoverageRegion,
  ReviewModel,
} from 'src/api/models';
import { fetchRegionsService } from 'src/api/region';
import { fetchReviewsService } from 'src/api/reviews';
import {
  fetchBlogPostsList,
  fetchAllBlogProfiles,
} from 'src/features/cms/prismic';
import { NeighborhoodLandingPage } from 'src/features/homeowners-neighborhood/components/landing-page/landing-page';
import type { NeighborhoodLandingPageProps } from 'src/features/homeowners-neighborhood/components/landing-page/landing-page';
import { getNeighborhoodLandingPageLayout } from 'src/features/homeowners-neighborhood/components/landing-page/landing-page-layout';
import { createClient } from 'src/prismicio';
import { ApiError } from 'src/utils/build-api-client';
import { buildServerClient } from 'src/utils/server-client';
import type { InitialZustandState } from 'src/store/region';

type StaticMethods = {
  getLayout?: (
    page: ReactNode,
    props: NeighborhoodLandingPageProps,
  ) => ReactNode;
};

type Neighborhood = {
  neighborhood: string;
};
type StaticProps = {
  neighborhoodData: Record<string, unknown>;
  cityData: Record<string, unknown>;
  listings: UnitListingModel[];
  initialZustandState?: InitialZustandState;
  blogPosts: PrismicDocument[];
  coverSrc: string;
  reviews: ReviewModel[];
  slug: string;
};

const HomeownersLandingPage: NextPage<NeighborhoodLandingPageProps> &
  StaticMethods = (props) => (
  <NeighborhoodLandingPage
    {...(props as PropsWithChildren<NeighborhoodLandingPageProps>)}
  />
);

HomeownersLandingPage.getLayout = getNeighborhoodLandingPageLayout;

export async function getStaticPaths() {
  // Keep this in case we decide to start building some paths at build time
  if (process.env.SKIP_BUILD_STATIC_GENERATION) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  return {
    paths: [],
    fallback: 'blocking',
  };
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const cityOrRegionSlug = params?.cityOrRegionSlug as string;
  const neighborhoodSlug = params?.neighborhoodSlug as string;

  if (!cityOrRegionSlug.endsWith('-property-management')) {
    return {
      redirect: {
        destination: `${BASE_PATHS.HOMEOWNER}/${cityOrRegionSlug}-property-management/${neighborhoodSlug}`,
        permanent: true,
      },
    };
  }

  const splitSlug = cityOrRegionSlug
    .replace('-property-management', '')
    .split('-');
  // `!` because `split` always yields at least one element, so this `pop`
  // cannot actually return `undefined` here.
  const stateId = splitSlug.pop()!.toUpperCase();
  const city = splitSlug
    .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
    .join(' ');
  const neighborhood = neighborhoodSlug
    .split('-')
    .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
    .join(' ');

  const apiClient = buildServerClient();

  try {
    let response: any;
    // `!` rather than a default: the `catch` below only returns for a 404, so
    // any other failure leaves this unassigned and the `find` further down
    // throws, which the bare `catch` in the next block swallows. Pre-existing;
    // see docs/development/typecheck-baseline-followups.md.
    let regions!: CoverageRegionModel[];

    try {
      [response, regions] = await Promise.all([
        fetchNeighborhoodService(city, stateId, neighborhoodSlug),
        fetchRegionsService(apiClient),
      ]);
    } catch (e) {
      if (e instanceof ApiError && e?.status === 404) {
        return {
          notFound: true,
        };
      }
    }

    let listings: UnitListingModel[] = [];
    // Initialized to `[]` like every other variable in this block — and like the
    // sibling region page — so a failure in the `try` below leaves an empty list
    // rather than the `undefined` Next then fails to serialize.
    let blogPosts: PrismicDocument[] = [];
    let coverSrc = '';
    let currentRegion: CoverageRegionModel | null = null;
    let reviews: ReviewModel[] = [];

    try {
      const client = createClient();
      if (response.cityModel.region) {
        currentRegion =
          regions.find(
            (region) => region.id === response.cityModel.region.id,
          ) ?? null;
      }

      if (!currentRegion) {
        return {
          notFound: true,
        };
      }

      const [
        { items },
        { url },
        { results: landingPageBlogPosts },
        blogProfiles,
        reviewsResponse,
      ] = await Promise.all([
        await fetchHomesService({
          city: [city],
          state: [stateId],
          neighborhood: [neighborhood],
          fallbackToBiggerArea: true,
          ...(currentRegion && {
            region: currentRegion.id,
          }),
          pageSize: 9,
        }),
        await fetchCityImageService({
          city,
          state: stateId as any,
          neighborhood,
          ...(currentRegion && {
            region: currentRegion.name as CoverageRegion['name'],
          }),
        }),
        await fetchBlogPostsList({
          page: 1,
          client,
          pageProps: { pageSize: 50 },
          tags: ['LP Featured Blog'],
        }),
        await fetchAllBlogProfiles(client),
        await fetchReviewsService({
          City: response.cityModel.city,
          Region: currentRegion.name as any,
          Neighborhood: neighborhood,
          ReviewPage: 'Neighborhood',
          NumberOfReviews: 6,
        }),
      ]);

      // `??` falls back to the same values these are initialized with above,
      // which is what the surrounding `try`/`catch` already relies on when the
      // requests fail. Assigning the raw `undefined` made Next fail to
      // serialize the props instead.
      listings = items ?? [];
      coverSrc = url ?? '';
      reviews = reviewsResponse;

      // Body is not needed here, so we are stripping it out
      const postsWithAutor = landingPageBlogPosts.map((post) => {
        const author = blogProfiles.find(
          (profile) => profile.uid === post?.data?.author,
        );
        const { body, ...postWithoutBody } = post.data;

        return {
          ...post,
          data: postWithoutBody,
          author,
        };
      });

      blogPosts = getRandomSampleFromArray({
        array: postsWithAutor,
        sampleSize: 6,
      });
    } catch {}

    const neighborhoodData = { ...response };
    const cityData = { ...response.cityModel };

    if (Array.isArray(cityData.neighborhoods)) {
      // Bound to a typed local before sorting: `response` is `any`, so mapping
      // straight off it left the comparator's parameters implicitly `any`.
      const neighborhoodLinks: { displayName: string; href: string }[] =
        cityData.neighborhoods.map((elem: Neighborhood) => {
          return {
            displayName: elem.neighborhood,
            href: `${BASE_PATHS.HOMEOWNER}/${cityOrRegionSlug}/${kebabCase(elem.neighborhood)}`,
          };
        });

      cityData.neighborhoods = neighborhoodLinks.sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      );
    } else {
      cityData.neighborhoods = [];
    }

    const slug = `${cityOrRegionSlug}/${neighborhoodSlug}`;

    const newProps: StaticProps = {
      neighborhoodData,
      cityData,
      listings,
      blogPosts,
      coverSrc,
      reviews,
      slug,
    };

    if (currentRegion) {
      newProps.initialZustandState = {
        region: currentRegion,
      };
    }

    return { props: newProps, revalidate: 60 * 60 * 24 }; // 1 day
  } catch (error) {
    console.log('[neighborhoodSlug] => getStaticProps => error', error);

    throw error;
  }
};

export default HomeownersLandingPage;
